import {type CSSProperties, type FocusEvent, useCallback, useLayoutEffect, useState} from "react";
import {defaultRangeExtractor, observeElementRect, useVirtualizer} from "@tanstack/react-virtual";
import {scrollContainerOf} from "./scrollContainer";
import {isWindowed, listStartInScroller, rowSpacing, type RowSpacing, viewHeight, withRetainedRow} from "./windowedList";

/**
 * Rows drawn beyond each edge of the visible part. Tab moves focus one row at a time and the browser
 * scrolls the focused row into view, so the next row a Tab reaches must already be drawn: this many
 * rows leaves room for a burst of key presses between two scroll events.
 */
const OVERSCAN_ROWS = 8;

/** Where each drawn row records its index, read back by the virtualizer and the focus tracking. */
const INDEX_ATTRIBUTE = "data-index";

/** Spread onto the outermost element of one row. Empty for a list short enough to draw in full. */
export interface WindowedRowProps {
	ref?: (element: Element | null) => void;
	[INDEX_ATTRIBUTE]?: number;
	style?: CSSProperties;
}

export interface WindowedRow {
	/** The row's position in the caller's own array. */
	index: number;
	props: WindowedRowProps;
}

export interface WindowedList<E extends HTMLElement> {
	/**
	 * On the element whose direct children are the rows. A callback ref, so a list that mounts after
	 * its owner rendered (inside a sheet's portal, say) still re-renders the owner and gets windowed.
	 */
	listRef: (element: E | null) => void;
	/** The rows to draw, in order: every row for a short list, the rows near the view for a long one. */
	rows: WindowedRow[];
	/** True when `rows` is a window; a list with list semantics then states its size per row. */
	windowed: boolean;
	/** On the list element: keeps the row holding keyboard focus drawn while it is scrolled away. */
	onFocus: (event: FocusEvent<HTMLElement>) => void;
	onBlur: (event: FocusEvent<HTMLElement>) => void;
}

interface Options {
	count: number;
	/** A row's height before it is measured; each drawn row is then measured, so this need not be exact. */
	estimateRowHeight: number;
	/** The list container's CSS `gap` between rows, in pixels; 0 for a block container. */
	gap?: number;
}

const DRAW_EVERY_ROW: WindowedRowProps = {};

/**
 * Draws a long file list as a window (kitecorp/transferify-ui#2942), keeping its own markup: the rows
 * stay the caller's elements in normal flow, and {@link rowSpacing}'s margins stand in for the rows
 * that are not drawn. The layout rules live in {@link ./windowedList}; this hook holds the state and
 * the measurements that feed them.
 */
export function useWindowedList<E extends HTMLElement>({count, estimateRowHeight, gap = 0}: Options): WindowedList<E> {
	const [list, listRef] = useState<E | null>(null);
	const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
	const [listStart, setListStart] = useState(0);
	// Nothing around the list scrolls, so there is no view to window against: draw every row instead.
	const [unscrolled, setUnscrolled] = useState(false);
	const windowed = isWindowed(count) && !unscrolled;

	const virtualizer = useVirtualizer<HTMLElement, Element>({
		count,
		// Not before the list is mounted: the virtualizer remembers a scroll offset from its first render and
		// scrolls its scroller there when it attaches. With no list there is no scroller to read, so it would
		// remember 0, and a list windowed against the PAGE (the sender's list below 520px of window height,
		// kitecorp/transferify-ui#2958, and on a phone while composing, kitecorp/transferify-ui#3410) would throw
		// the page back to its top as it opened.
		enabled: windowed && list !== null,
		// Where the scroller already is, read when the virtualizer attaches, so attaching moves nothing; the
		// pattern TanStack's own window virtualizer uses for the window.
		initialOffset: () => scrollContainerOf(list)?.scrollTop ?? 0,
		getScrollElement: () => scrollContainerOf(list),
		estimateSize: () => estimateRowHeight,
		// A list sized by its own rows measures 0 before any is drawn; see viewHeight.
		initialRect: {width: 0, height: viewHeight(0, estimateRowHeight)},
		observeElementRect: (instance, report) => observeElementRect(instance, rect =>
			report({width: rect.width, height: viewHeight(rect.height, estimateRowHeight)})),
		gap,
		overscan: OVERSCAN_ROWS,
		scrollMargin: listStart,
		indexAttribute: INDEX_ATTRIBUTE,
		rangeExtractor: range => withRetainedRow(defaultRangeExtractor(range), focusedIndex, range.count),
		// React 19 reports a flushSync issued from a layout effect as an error; a scroll-driven render
		// one frame later is covered by the overscan rows.
		useFlushSync: false,
	});
	const items = windowed ? virtualizer.getVirtualItems() : [];
	const spacing = rowSpacing(items, listStart, virtualizer.getTotalSize(), gap);

	// Runs after every render: the content above a list inside a larger scroller can move at any time,
	// and reading two rectangles is cheaper than tracking everything that could move it.
	useLayoutEffect(() => {
		if (!windowed || list === null) {
			return;
		}
		const scroller = scrollContainerOf(list);
		if (scroller === null) {
			setUnscrolled(true);
			return;
		}
		const firstRow = list.querySelector<HTMLElement>(`:scope > [${INDEX_ATTRIBUTE}]`);
		const position = firstRow === null
			? -1
			: items.findIndex(item => item.index === Number(firstRow.getAttribute(INDEX_ATTRIBUTE)));
		if (firstRow === null || position < 0) {
			return;
		}
		const measured = listStartInScroller({
			scrollerTop: scroller.getBoundingClientRect().top,
			scrollerBorderTop: scroller.clientTop,
			scrollTop: scroller.scrollTop,
			firstRowTop: firstRow.getBoundingClientRect().top,
			firstRowOffset: items[position].start - listStart,
		});
		// Whole pixels only: sub-pixel layout would otherwise re-render this list forever.
		if (Math.abs(measured - listStart) >= 1) {
			setListStart(measured);
		}
	});

	const onFocus = useCallback((event: FocusEvent<HTMLElement>) => {
		const row = (event.target as Element).closest(`[${INDEX_ATTRIBUTE}]`);
		if (row !== null && event.currentTarget.contains(row)) {
			setFocusedIndex(Number(row.getAttribute(INDEX_ATTRIBUTE)));
		}
	}, []);
	const onBlur = useCallback((event: FocusEvent<HTMLElement>) => {
		const next = event.relatedTarget;
		if (!(next instanceof Node) || !event.currentTarget.contains(next)) {
			setFocusedIndex(null);
		}
	}, []);

	const rows: WindowedRow[] = windowed
		? items.map((item, position) => ({
			index: item.index,
			props: {ref: virtualizer.measureElement, [INDEX_ATTRIBUTE]: item.index, style: marginsOf(spacing[position])},
		}))
		: Array.from({length: count}, (_, index) => ({index, props: DRAW_EVERY_ROW}));

	return {listRef, rows, windowed, onFocus, onBlur};
}

function marginsOf({marginTop, marginBottom}: RowSpacing): CSSProperties | undefined {
	if (marginTop === 0 && marginBottom === 0) {
		return undefined;
	}
	return {marginTop, marginBottom};
}
